import { useForm } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus } from '@untitled-ui/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../../../../../components/buttons/button';
import { ToastVariant } from '../../../../../../components/toast';
import {
  OrganizationInviteStatus,
  OrganizationUserRole,
} from '../../../../../../entity/api/organization-user';
import { BadRequestError, ForbiddenError } from '../../../../../../error';
import { convertError } from '../../../../../../error/convert';
import { addTrackCrumb, trackError } from '../../../../../../error/tracking';
import { captureException } from '../../../../../../logger/capture-exception';
import { getQueryClient } from '../../../../../../sdk/request';
import { PostHogEvents, capturePostHogEvent } from '../../../../../../services/posthog';
import {
  getAuthUserFromCache,
  useInviteUsersToOrganization,
  useManageOrganizationUser,
  useOrganizationUsersList,
  useRevokeInvite,
  useTransferOwnership,
  useUpdateUser,
  useUsersByIds,
} from '../../../../../../services/storage';
import {
  getBrandsListFromCache,
  useBrandsList,
} from '../../../../../../services/storage/brands-queries';
import { useCurrentOrgUserRole } from '../../../../../../services/storage/organization-users-queries';
import { useOrganizationById } from '../../../../../../services/storage/organizations-queries';
import { tNamespace } from '../../../../../../services/translation';
import { useToast } from '../../../../../../shared/contexts/toast-context';
import { getSelectedOrganization } from '../../../../../../shared/organization';
import { UserCantInviteDialog } from './../-components/user-cant-invite';
import { UserDeleteUserDialog } from './../-components/user-delete-user-dialog';
import { UserInviteUserDialog } from './../-components/user-invite-user-dialog';
import { UserTransferOwnershipDialog } from './../-components/user-transfer-ownership-dialog';
import { UserComponent } from './../-components/user-user-component';
import { UserUserInvited } from './../-components/user-user-invited';

const t = tNamespace('app.settings.organization.users');
const tErrors = tNamespace('errors');

export type UserInviteForm = {
  email: string;
};

function handleRedirectToBilling(navigate: ReturnType<typeof useNavigate>, id: string) {
  navigate({ to: `/app/settings/organization/${id}/plan`, params: { id } }).catch(function (err) {
    captureException(
      err,
      {
        file: 'app/settings/organization.$id/users.tsx',
        method: 'handleRedirectToBilling',
        infoMessage: 'unable to redirect to billing',
      },
      trackError
    );
  });
}

export const Route = createFileRoute(
  '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/users'
)({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/users',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: UserTab,
});

export function UserTab() {
  const { id } = Route.useParams() as unknown as { id: string };

  const [dialogInviteState, setDialogInviteState] = useState<boolean>(false);
  const [dialogDeleteState, setDialogDeleteState] = useState<boolean>(false);
  const [userIdToDelete, setUserIdToDelete] = useState<string | null>(null);
  const [userIdToTransfer, setUserIdToTransfer] = useState<string | null>(null);
  const [dialogTransferState, setDialogTransferState] = useState<boolean>(false);
  const [dialogCantInviteState, setDialogCantInviteState] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const addToast = useToast();
  const navigate = useNavigate();

  // TODO: ask the rules of when to invites or not user
  const [canInvite, _setCanInvite] = useState<boolean>(true);

  // Ref to track if an update is in progress to prevent concurrent calls
  const isUpdatingUserRef = useRef<boolean>(false);
  const lastUpdateParamsRef = useRef<{ orgId: string; brandId: string } | null>(null);

  // TanStack Query hooks
  const queryClient = getQueryClient();
  const { data: organization } = useOrganizationById(id);
  const { data: organizationUsersData } = useOrganizationUsersList(id);
  const organizationUsersArray = useMemo(
    () => organizationUsersData ?? [],
    [organizationUsersData]
  );
  const organizationUsers = { data: organizationUsersArray };
  const currentUser = getAuthUserFromCache(queryClient);

  const { isOwner, currentUserOrganization } = useCurrentOrgUserRole(id, currentUser?._id);

  // TanStack Query mutations
  const inviteUsersMutation = useInviteUsersToOrganization();
  const manageUserMutation = useManageOrganizationUser();
  const transferOwnershipMutation = useTransferOwnership();
  const revokeInviteMutation = useRevokeInvite();
  const updateUserMutation = useUpdateUser();
  // Keep the hook to ensure brands are fetched and cached
  useBrandsList();

  const invitedUsers = useMemo(
    function () {
      return organizationUsersArray.filter(function (orgUser) {
        return orgUser?.inviteStatus !== OrganizationInviteStatus.Accepted;
      });
    },
    [organizationUsersArray]
  );

  // Extract user IDs for accepted organization users
  const acceptedUserIds = useMemo(
    function () {
      const userIds: string[] = [];
      for (const orgUser of organizationUsersArray) {
        if (orgUser?.userId != null && orgUser.inviteStatus === OrganizationInviteStatus.Accepted) {
          userIds.push(orgUser.userId);
        }
      }
      return userIds;
    },
    [organizationUsersArray]
  );

  // TanStack Query hook for fetching users by IDs
  const { data: usersData } = useUsersByIds(acceptedUserIds);
  const users = usersData;

  const form = useForm<UserInviteForm>({
    defaultValues: {
      email: '',
    },
    onSubmit: function (data) {
      const email = (data.value?.email ?? '').trim().toLowerCase();
      if (email.length === 0 || organization?._id == null) {
        return;
      }

      setIsLoading(true);

      inviteUsersMutation.mutate(
        { organizationId: organization._id, userEmails: [email] },
        {
          onSuccess: function (data) {
            capturePostHogEvent(PostHogEvents.MEMBER_INVITED, {
              inviteeRole: 'member',
              workspaceSizeAfter: data.orgUsers.length,
            });

            setDialogInviteState(false);
            addToast({
              variant: ToastVariant.Success,
              title: t('titleInvitedUser'),
              id: Date.now(),
            });
          },
          onError: function (err) {
            const handledErr = convertError(err);
            // TODO: we should have app error codes
            if (
              handledErr instanceof BadRequestError &&
              handledErr.originalMessage
                .toLowerCase()
                .replace(/ /g, '')
                .indexOf('alreadymember') !== -1
            ) {
              addToast({
                variant: ToastVariant.Error,
                title: t('inviteMemberExistsFailed'),
                id: Date.now(),
              });
              return;
            }

            // handle forbiddens (which comes from plan features limitations)
            // as such, the original message is a specific enum incoming
            if (handledErr instanceof ForbiddenError) {
              addToast({
                variant: ToastVariant.Error,
                title: tErrors(handledErr.originalMessage),
                id: Date.now(),
              });
              return;
            }

            // handle the generic invitaiton error
            addToast({
              variant: ToastVariant.Error,
              title: t('inviteFailed'),
              id: Date.now(),
            });

            captureException(
              err,
              {
                file: 'user-tab.tsx',
                method: 'inviteUser',
                infoMessage: 'unable to invite user',
              },
              trackError
            );
          },
          onSettled: function () {
            setIsLoading(false);
          },
        }
      );
    },
  });

  function deleteUser() {
    if (userIdToDelete == null || organization?._id == null) {
      return;
    }

    manageUserMutation.mutate(
      {
        organizationId: organization._id,
        data: {
          userId: userIdToDelete,
          role: OrganizationUserRole.Member,
          delete: true,
          roleLabel: currentUserOrganization?.roleLabel ?? '',
        },
      },
      {
        onSuccess: function () {
          addToast({
            variant: ToastVariant.Success,
            title: t('toastTitleDeletedUser'),
            id: Date.now(),
          });
          setUserIdToDelete(null);
          setDialogDeleteState(false);
          // No need to reload - TanStack Query cache is automatically updated
        },
        onError: function (err) {
          addToast({
            variant: ToastVariant.Error,
            title: t('deleteUserFailed'),
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'user-tab.tsx',
              method: 'deleteUser',
              infoMessage: 'unable to delete user',
            },
            trackError
          );
        },
      }
    );
  }

  function transferOwnership() {
    if (userIdToTransfer == null || organization?._id == null) {
      return;
    }

    transferOwnershipMutation.mutate(
      { organizationId: organization._id, toUserId: userIdToTransfer },
      {
        onSuccess: function () {
          addToast({
            variant: ToastVariant.Success,
            title: t('toastTitleTransferredOwnership'),
            id: Date.now(),
          });
          setDialogTransferState(false);
          // No need to reload - TanStack Query cache is automatically updated
        },
        onError: function (err) {
          addToast({
            variant: ToastVariant.Error,
            title: t('transferOwnershipFailed'),
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'user-tab.tsx',
              method: 'transferOwnership',
              infoMessage: 'unable to transfer ownership',
            },
            trackError
          );
        },
      }
    );
  }

  function revokeInviteFunction(email: string) {
    if (organization?._id == null) {
      return;
    }

    revokeInviteMutation.mutate(
      { organizationId: organization._id, userEmail: email },
      {
        onSuccess: function () {
          addToast({
            variant: ToastVariant.Success,
            title: t('toastTitleRevokedInvite'),
            id: Date.now(),
          });
          // No need to reload - TanStack Query cache is automatically updated
        },
        onError: function (err) {
          addToast({
            variant: ToastVariant.Error,
            title: t('revokeInviteFailed'),
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'user-tab.tsx',
              method: 'revokeInvite',
              infoMessage: 'unable to revoke invite',
            },
            trackError
          );
        },
      }
    );
  }

  function resendInviteFunction(email: string) {
    if (organization?._id == null) {
      return;
    }

    inviteUsersMutation.mutate(
      { organizationId: organization._id, userEmails: [email] },
      {
        onSuccess: function () {
          addToast({
            variant: ToastVariant.Success,
            title: t('toastTitleResendedInvite'),
            id: Date.now(),
          });
        },
        onError: function (err) {
          addToast({
            variant: ToastVariant.Error,
            title: t('resendInviteFailed'),
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'user-tab.tsx',
              method: 'resendInvite',
              infoMessage: 'unable to resend invite',
            },
            trackError
          );
        },
      }
    );
  }

  useEffect(
    function () {
      // Skip if user is not available yet
      if (currentUser == null) {
        return;
      }

      // Prevent concurrent calls - if an update is already in progress, skip
      if (isUpdatingUserRef.current === true) {
        return;
      }

      // Check if user's selected organization already matches the URL param
      // This prevents redundant calls when switchToOrganization already updated the user
      if (currentUser.selectedOrganizationId === id) {
        // Also verify the brand is correct for this organization
        const queryClient = getQueryClient();
        const currentBrandsList = getBrandsListFromCache(queryClient) ?? [];
        const brandForOrg = currentBrandsList.find((b) => b?.organizationId === id);

        // If user's selected brand matches a brand in this org, no update needed
        if (brandForOrg != null && currentUser.selectedBrandId === brandForOrg._id) {
          return;
        }
      }

      // Use getSelectedOrganization as a fallback check (for edge cases)
      const currentOrg = getSelectedOrganization(currentUser);
      if (currentOrg?._id === id) {
        return;
      }

      // Use imperative cache access to get current brands data at runtime
      // This avoids stale closure issues when org switches
      const queryClient = getQueryClient();
      const currentBrandsList = getBrandsListFromCache(queryClient) ?? [];
      const brand = currentBrandsList.find((b) => b?.organizationId === id);

      // Only update if the values would actually change
      const newOrgId = id ?? '';
      const newBrandId = brand?._id ?? '';

      if (
        currentUser.selectedOrganizationId === newOrgId &&
        currentUser.selectedBrandId === newBrandId
      ) {
        // Values are already correct, no need to update
        return;
      }

      // Check if we're trying to update to the same values as the last update
      // This prevents rapid-fire calls when the cache updates trigger re-renders
      if (
        lastUpdateParamsRef.current != null &&
        lastUpdateParamsRef.current.orgId === newOrgId &&
        lastUpdateParamsRef.current.brandId === newBrandId
      ) {
        return;
      }

      // Re-read from cache right before PATCH — avoids duplicate when organization-switcher
      // (or another path) already updated selection; currentUser from the hook can be stale.
      const freshUser = getAuthUserFromCache(queryClient);
      if (
        freshUser?.selectedOrganizationId === newOrgId &&
        freshUser?.selectedBrandId === newBrandId
      ) {
        return;
      }

      // Mark that an update is in progress
      isUpdatingUserRef.current = true;
      lastUpdateParamsRef.current = { orgId: newOrgId, brandId: newBrandId };

      updateUserMutation
        .mutateAsync({
          selectedBrandId: newBrandId,
          selectedOrganizationId: newOrgId,
        })
        .then(function () {
          // Reset the flag after a short delay to allow cache updates to propagate
          setTimeout(function () {
            isUpdatingUserRef.current = false;
          }, 100);
        })
        .catch(function (err) {
          // Reset the flag on error
          isUpdatingUserRef.current = false;
          captureException(
            err,
            {
              file: 'user-tab.tsx',
              method: 'updateUser',
              infoMessage: 'unable to update user',
            },
            trackError
          );
        });
    },
    // Watch id and currentUser, but use specific fields for comparison to prevent unnecessary runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, currentUser?.selectedOrganizationId, currentUser?.selectedBrandId]
  );

  if (organization == null || organizationUsers == null || currentUser == null) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col pb-20">
        <div className="mb-6 flex items-center justify-between">
          <p className="font-display-4">{t('users')}</p>
          {isOwner ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={function () {
                if (canInvite) {
                  setDialogInviteState(true);
                } else {
                  setDialogCantInviteState(true);
                }
              }}
              leftIcon={<Plus width={16} height={16} />}
            >
              {t('addUser')}
            </Button>
          ) : null}
        </div>
        <div className="flex flex-col gap-4 rounded-md border border-branding-gray p-4">
          {(users ?? []).map(function (user) {
            if (user == null) {
              return null;
            }

            const orgUser = organizationUsers.data.find((orgUser) => orgUser?.userId === user._id);
            if (orgUser?.role === OrganizationUserRole.SuperAdmin) {
              return null;
            }

            const role = orgUser?.role ?? OrganizationUserRole.Member;
            return (
              <UserComponent
                key={user._id}
                userInformation={user}
                role={role}
                isOwner={isOwner}
                onRemove={function () {
                  setUserIdToDelete(user._id);
                  setDialogDeleteState(true);
                }}
                onEdit={function () {
                  setUserIdToTransfer(user._id);
                  setDialogTransferState(true);
                }}
              />
            );
          })}
          {invitedUsers.map(function (user) {
            if (user == null) {
              return null;
            }

            return (
              <UserUserInvited
                key={user._id}
                email={user.userEmail}
                onEdit={function () {
                  resendInviteFunction(user.userEmail);
                }}
                revokeInvite={function () {
                  revokeInviteFunction(user.userEmail);
                }}
              />
            );
          })}
        </div>
      </div>

      <UserInviteUserDialog
        dialogInviteState={dialogInviteState}
        setDialogInviteState={setDialogInviteState}
        form={form}
        handleInviteUser={function () {
          form.handleSubmit().catch(function (err) {
            captureException(
              err,
              {
                file: 'user-tab.tsx',
                method: 'handleInviteUser',
                infoMessage: 'unable to invite user',
              },
              trackError
            );
          });
        }}
        isLoading={isLoading}
      />

      <UserDeleteUserDialog
        dialogDeleteState={dialogDeleteState}
        setDialogDeleteState={setDialogDeleteState}
        isLoading={isLoading}
        deleteUser={function () {
          deleteUser();
        }}
      />
      <UserCantInviteDialog
        dialogCantInviteState={dialogCantInviteState}
        setDialogCantInviteState={setDialogCantInviteState}
        handleRedirectToBilling={function () {
          handleRedirectToBilling(navigate, id);
        }}
      />

      <UserTransferOwnershipDialog
        dialogTransferState={dialogTransferState}
        setDialogTransferState={setDialogTransferState}
        isLoading={isLoading}
        transferOwnership={function () {
          transferOwnership();
        }}
      />
    </>
  );
}
